<template>
  <div class="detail-container work-conversion-preview">
    <div class="d-flex justify-content-between align-items-start mb-3">
      <div>
        <h1 class="page-title mb-1">作業作成プレビュー</h1>
        <div v-if="preview" class="text-muted">{{ preview.estimate.estimate_no }}</div>
      </div>
      <router-link
        class="btn btn-outline-secondary"
        :to="{ name: 'EstimateDetail', params: { id: route.params.id }, query: route.query }"
      >
        見積詳細へ戻る
      </router-link>
    </div>

    <div
      v-if="allErrors.length"
      ref="errorSummary"
      class="alert alert-danger error-summary"
      role="alert"
      tabindex="-1"
    >
      <div class="fw-bold mb-1">入力内容を確認してください。</div>
      <div v-for="message in allErrors" :key="message">・{{ message }}</div>
    </div>

    <div v-if="isLoading" class="text-center py-5">プレビューを読み込み中...</div>

    <template v-else-if="preview">
      <div class="row g-3 mb-3">
        <div class="col-lg-7">
          <div class="card h-100">
            <div class="card-header fw-bold">作成元の見積</div>
            <div class="card-body">
              <dl class="row mb-0">
                <dt class="col-sm-3">見積No</dt>
                <dd class="col-sm-9">{{ preview.estimate.estimate_no }}</dd>
                <dt class="col-sm-3">件名</dt>
                <dd class="col-sm-9">{{ preview.estimate.title }}</dd>
                <dt class="col-sm-3">顧客</dt>
                <dd class="col-sm-9">{{ preview.estimate.customer?.name || '-' }}</dd>
                <dt class="col-sm-3">建物</dt>
                <dd class="col-sm-9">{{ preview.estimate.building?.name || '-' }}</dd>
                <dt class="col-sm-3">見積税込金額</dt>
                <dd class="col-sm-9 fw-bold">{{ formatCurrency(preview.estimate.total_amount) }}</dd>
              </dl>
            </div>
          </div>
        </div>
        <div class="col-lg-5">
          <div class="card h-100">
            <div class="card-header fw-bold">金額差額チェック</div>
            <div class="card-body">
              <table class="table table-sm amount-table mb-2">
                <tbody>
                  <tr>
                    <th>税抜見積金額</th>
                    <td>{{ formatCurrency(preview.amounts.estimate_amount) }}</td>
                  </tr>
                  <tr v-if="isDifferenceConversion">
                    <th>前回作業化済み見積金額</th>
                    <td>{{ formatCurrency(preview.amounts.baseline_estimate_amount) }}</td>
                  </tr>
                  <tr v-if="isDifferenceConversion">
                    <th>今回の改訂差額</th>
                    <td>{{ formatSignedCurrency(preview.amounts.revision_difference_amount) }}</td>
                  </tr>
                  <tr>
                    <th>{{ isDifferenceConversion ? '追加作業作成予定金額' : '作業変換予定金額' }}</th>
                    <td>{{ formatCurrency(selectedConversionAmount) }}</td>
                  </tr>
                  <tr class="fw-bold">
                    <th>{{ isDifferenceConversion ? '作業化しない差額' : '差額' }}</th>
                    <td :class="{ 'text-danger': selectedDifferenceAmount !== 0 }">
                      {{ formatSignedCurrency(selectedDifferenceAmount) }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="small text-muted">{{ preview.amounts.comparison_basis }}</div>
              <div class="small text-muted">
                差額には「作業化しない」明細や値引・調整費が含まれる場合があります。
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isDifferenceConversion" class="alert alert-info">
        <strong>
          {{ baselineEstimateLabel }}で作業作成済みのため、今回の改訂差分だけを作業登録します。
        </strong>
        既存の作業・請求データは変更しません。減額差分は新しい作業を作成せず、下の確認欄へ表示します。
      </div>
      <div v-if="isDifferenceConversion && !hasCandidates" class="alert alert-warning">
        追加・増額となる差分がないため、新しい作業は作成できません。既存作業の変更が必要な場合は個別に確認してください。
      </div>

      <div class="card mb-3">
        <div class="card-header fw-bold">見積明細と作成先</div>
        <div class="table-responsive">
          <table class="table table-sm table-bordered align-middle mb-0">
            <thead>
              <tr>
                <th>セクション</th>
                <th>明細</th>
                <th>区分</th>
                <th>金額</th>
                <th>作成先区分</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in preview.source_lines" :key="line.id">
                <td>{{ line.section_name || 'セクション外' }}</td>
                <td>{{ line.item_name || '-' }}</td>
                <td>{{ line.business_classification_name || '-' }}</td>
                <td class="text-end">{{ formatCurrency(line.amount) }}</td>
                <td>
                  <span class="badge" :class="targetBadgeClass(line.work_target_type)">
                    {{ targetLabel(line.work_target_type) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="hasCandidates" class="card mb-3 conversion-settings-card">
        <div class="card-header fw-bold">作成対象と作成単位</div>
        <div class="card-body">
          <p class="small text-muted mb-3">
            作業を作成する見積項目だけを選択し、作成単位を指定してください。
          </p>
          <div class="row g-2 mb-3">
            <div
              v-for="candidate in allConversionCandidates"
              :key="`selection-${candidate.candidate_key}`"
              class="col-lg-6"
            >
              <label class="candidate-selection border rounded p-2 w-100">
                <input
                  v-model="candidate.selected"
                  class="form-check-input me-2"
                  type="checkbox"
                  @change="selectionChanged"
                >
                <span class="flex-grow-1">{{ candidate.name }}</span>
                <span class="text-nowrap ms-2">{{ formatCurrency(candidate.amount) }}</span>
              </label>
            </div>
          </div>
          <div class="border-top pt-3 d-flex flex-wrap align-items-center gap-3">
            <span class="fw-bold">作成単位</span>
            <label class="form-check mb-0">
              <input
                v-model="creationMode"
                class="form-check-input"
                type="radio"
                value="individual"
                @change="creationModeChanged"
              >
              <span class="form-check-label">選択項目ごとに作成</span>
            </label>
            <label class="form-check mb-0">
              <input
                v-model="creationMode"
                class="form-check-input"
                type="radio"
                value="combined"
                @change="creationModeChanged"
              >
              <span class="form-check-label">選択項目を1件にまとめて作成</span>
            </label>
            <label class="d-flex align-items-center gap-2 mb-0">
              <span class="fw-bold text-nowrap">月別金額の端数処理</span>
              <select
                v-model="amountRoundingType"
                class="form-select form-select-sm"
                @change="amountRoundingTypeChanged"
              >
                <option value="floor">切り捨て</option>
                <option value="round">四捨五入</option>
                <option value="ceil">切り上げ</option>
              </select>
            </label>
            <span class="ms-lg-auto small fw-bold">
              {{ selectedCandidateCount }}項目を {{ plannedWorkCount }}件の作業として作成
            </span>
          </div>
        </div>
      </div>

      <section class="mb-4">
        <div class="d-flex align-items-center gap-2 mb-2">
          <h2 class="h4 mb-0">年間予定作業</h2>
          <span class="badge text-bg-primary">{{ activeAnnualCandidates.length }}件作成</span>
        </div>
        <div v-if="annualCandidates.length === 0" class="alert alert-light border">
          年間予定へ変換する候補はありません。
        </div>
        <div v-else-if="activeAnnualCandidates.length === 0" class="alert alert-light border">
          年間予定の作成対象が選択されていません。
        </div>
        <div
          v-for="candidate in activeAnnualCandidates"
          :key="candidate.candidate_key"
          class="card mb-3 candidate-card"
        >
          <div class="card-header d-flex justify-content-between align-items-center">
            <strong>{{ candidate.name }}</strong>
            <span class="fw-bold">
              {{ isDifferenceConversion ? '追加年額' : '年額' }} {{ formatCurrency(candidate.amount) }}
            </span>
          </div>
          <div class="card-body">
            <div v-if="isDifferenceConversion" class="small text-muted mb-3">
              改訂後 {{ formatCurrency(candidate.full_amount) }} －
              前回作業化済み {{ formatCurrency(candidate.baseline_amount) }}
            </div>
            <div class="row g-3 mb-3">
              <div class="col-md-4"><span class="field-label">顧客</span>{{ preview.estimate.customer?.name || '-' }}</div>
              <div class="col-md-4"><span class="field-label">建物</span>{{ preview.estimate.building?.name || '-' }}</div>
              <div class="col-md-4">
                <span
                  class="field-label"
                  :class="{ 'required-label': needsClassificationSelection(candidate) }"
                >業務区分</span>
                <select
                  v-if="needsClassificationSelection(candidate)"
                  v-model="candidate.m_business_classification_id"
                  class="form-select"
                >
                  <option value="">選択してください</option>
                  <option
                    v-for="classification in businessClassifications"
                    :key="classification.id"
                    :value="classification.id"
                  >
                    {{ classification.business_classification_nm }}
                  </option>
                </select>
                <span v-else>{{ candidate.business_classification?.name || '-' }}</span>
                <div
                  v-if="!candidate.business_classification?.id"
                  class="form-text text-danger"
                >見積書で未設定のため、作業作成前に選択してください。</div>
              </div>
              <div v-if="candidate.is_combined" class="col-12">
                <label class="form-label required-label">作業名</label>
                <input v-model.trim="candidate.name" class="form-control" type="text">
              </div>
              <div class="col-12"><span class="field-label">業務内容・内訳</span><div class="pre-line">{{ candidate.breakdown || candidate.name }}</div></div>
              <div class="col-md-3">
                <label class="form-label">頻度</label>
                <input v-model.trim="candidate.work_frequency" class="form-control" type="text" placeholder="例：毎月、年4回">
              </div>
              <div class="col-md-3">
                <label class="form-label required-label">年間実施回数</label>
                <input
                  v-model="candidate.annual_count"
                  class="form-control"
                  type="number"
                  min="1"
                  max="12"
                  step="1"
                  @change="annualCountChanged(candidate)"
                >
              </div>
              <div class="col-md-3">
                <label class="form-label">1回あたり実施数</label>
                <input v-model="candidate.service_count" class="form-control" type="number" min="0" step="1">
              </div>
              <div class="col-md-3">
                <label class="form-label required-label">作業年度（開始年）</label>
                <input
                  v-model="candidate.work_year"
                  class="form-control"
                  type="number"
                  min="2000"
                  max="2100"
                  step="1"
                  @change="workYearChanged(candidate)"
                >
              </div>
            </div>

            <div class="form-check mb-3">
              <input
                :id="`annual-months-pending-${candidate.candidate_key}`"
                v-model="candidate.execution_months_pending"
                class="form-check-input"
                type="checkbox"
                @change="annualSchedulePendingChanged(candidate)"
              >
              <label :for="`annual-months-pending-${candidate.candidate_key}`" class="form-check-label fw-bold">
                実施月未設定（実施月は年間予定表で後から設定）
              </label>
              <div class="form-text">未設定の間は月別請求の対象になりません。</div>
            </div>

            <div class="table-responsive mb-3">
              <table class="table table-sm table-bordered month-table align-middle mb-0">
                <thead>
                  <tr><th>実施</th><th>実施月</th><th>月別金額</th></tr>
                </thead>
                <tbody>
                  <tr v-for="month in candidate.months" :key="`${month.work_year}-${month.work_month}`">
                    <td class="text-center">
                      <input
                        v-model="month.selected"
                        class="form-check-input"
                        type="checkbox"
                        :disabled="candidate.execution_months_pending"
                        :aria-label="`${month.work_year}年${month.work_month}月を実施`"
                        @change="monthsChanged(candidate)"
                      >
                    </td>
                    <td>{{ month.work_year }}年{{ month.work_month }}月</td>
                    <td>
                      <div class="input-group input-group-sm">
                        <input
                          v-model="month.bill_amount"
                          class="form-control text-end"
                          type="number"
                          min="0"
                          step="1"
                          :disabled="candidate.execution_months_pending || !month.selected"
                        >
                        <span class="input-group-text">円</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="fw-bold">
                    <th colspan="2">選択 {{ selectedMonths(candidate).length }}か月／月別合計</th>
                    <td class="text-end">{{ formatCurrency(monthAmountTotal(candidate)) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div class="border rounded p-3 bg-light">
              <div class="form-check mb-2">
                <input
                  :id="`annual-outsourced-${candidate.candidate_key}`"
                  v-model="candidate.is_outsourced"
                  class="form-check-input"
                  type="checkbox"
                >
                <label :for="`annual-outsourced-${candidate.candidate_key}`" class="form-check-label fw-bold">外注あり</label>
              </div>
              <div v-if="candidate.is_outsourced" class="row g-3">
                <div class="col-md-6">
                  <label class="form-label required-label">協力会社／外注業者</label>
                  <select v-model="candidate.m_vendor_id" class="form-select">
                    <option value="">選択してください</option>
                    <option v-for="vendor in vendors" :key="vendor.id" :value="vendor.id">{{ vendor.vendor_nm }}</option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label required-label">年間外注金額</label>
                  <div class="input-group">
                    <input v-model="candidate.request_amount" class="form-control text-end" type="number" min="0" step="1">
                    <span class="input-group-text">円</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="mb-4">
        <div class="d-flex align-items-center gap-2 mb-2">
          <h2 class="h4 mb-0">臨時作業</h2>
          <span class="badge text-bg-warning">{{ activeTemporaryCandidates.length }}件作成</span>
        </div>
        <div v-if="temporaryCandidates.length === 0" class="alert alert-light border">
          臨時作業へ変換する候補はありません。
        </div>
        <div v-else-if="activeTemporaryCandidates.length === 0" class="alert alert-light border">
          臨時作業の作成対象が選択されていません。
        </div>
        <div
          v-for="candidate in activeTemporaryCandidates"
          :key="candidate.candidate_key"
          class="card mb-3 candidate-card"
        >
          <div class="card-header d-flex justify-content-between align-items-center">
            <strong>{{ candidate.name }}</strong>
            <span class="fw-bold">
              {{ isDifferenceConversion ? '追加受注金額' : '受注金額' }} {{ formatCurrency(candidate.amount) }}
            </span>
          </div>
          <div class="card-body">
            <div v-if="isDifferenceConversion" class="small text-muted mb-3">
              改訂後 {{ formatCurrency(candidate.full_amount) }} －
              前回作業化済み {{ formatCurrency(candidate.baseline_amount) }}
            </div>
            <div class="row g-3 mb-3">
              <div class="col-md-3"><span class="field-label">見積No</span>{{ preview.estimate.estimate_no }}</div>
              <div class="col-md-3"><span class="field-label">建物</span>{{ preview.estimate.building?.name || '-' }}</div>
              <div class="col-md-3">
                <span
                  class="field-label"
                  :class="{ 'required-label': needsClassificationSelection(candidate) }"
                >業務区分</span>
                <select
                  v-if="needsClassificationSelection(candidate)"
                  v-model="candidate.m_business_classification_id"
                  class="form-select"
                >
                  <option value="">選択してください</option>
                  <option
                    v-for="classification in businessClassifications"
                    :key="classification.id"
                    :value="classification.id"
                  >
                    {{ classification.business_classification_nm }}
                  </option>
                </select>
                <span v-else>{{ candidate.business_classification?.name || '-' }}</span>
                <div
                  v-if="!candidate.business_classification?.id"
                  class="form-text text-danger"
                >見積書で未設定のため、作業作成前に選択してください。</div>
              </div>
              <div class="col-md-3"><span class="field-label">受注日</span>{{ formatDate(preview.estimate.ordered_at) }}</div>
              <div v-if="candidate.is_combined" class="col-12">
                <label class="form-label required-label">作業名</label>
                <input v-model.trim="candidate.name" class="form-control" type="text">
              </div>
              <div class="col-12"><span class="field-label">業務内容・内訳</span><div class="pre-line">{{ candidate.breakdown || candidate.name }}</div></div>
              <div class="col-md-6">
                <label class="form-label required-label">作業日</label>
                <input v-model="candidate.work_dt" class="form-control" type="date">
              </div>
              <div class="col-md-6">
                <label class="form-label required-label">請求月</label>
                <input v-model="candidate.billing_period" class="form-control" type="month">
              </div>
            </div>
            <div class="border rounded p-3 bg-light">
              <div class="form-check mb-2">
                <input
                  :id="`temporary-outsourced-${candidate.candidate_key}`"
                  v-model="candidate.is_outsourced"
                  class="form-check-input"
                  type="checkbox"
                >
                <label :for="`temporary-outsourced-${candidate.candidate_key}`" class="form-check-label fw-bold">外注あり</label>
              </div>
              <div v-if="candidate.is_outsourced" class="row g-3">
                <div class="col-md-6">
                  <label class="form-label required-label">外注業者</label>
                  <select v-model="candidate.m_vendor_id" class="form-select">
                    <option value="">選択してください</option>
                    <option v-for="vendor in vendors" :key="vendor.id" :value="vendor.id">{{ vendor.vendor_nm }}</option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label required-label">外注金額</label>
                  <div class="input-group">
                    <input v-model="candidate.request_amount" class="form-control text-end" type="number" min="0" step="1">
                    <span class="input-group-text">円</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="excludedDifferences.length" class="mb-4">
        <div class="d-flex align-items-center gap-2 mb-2">
          <h2 class="h4 mb-0">作業を追加作成しない改訂差分</h2>
          <span class="badge text-bg-secondary">{{ excludedDifferences.length }}件</span>
        </div>
        <div class="card">
          <div class="table-responsive">
            <table class="table table-sm table-bordered align-middle mb-0">
              <thead>
                <tr>
                  <th>工事項目</th>
                  <th>前回作業化済み</th>
                  <th>改訂後</th>
                  <th>差額</th>
                  <th>取扱い</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="difference in excludedDifferences" :key="difference.lineage_key">
                  <td>{{ difference.name }}</td>
                  <td class="text-end">{{ formatCurrency(difference.baseline_amount) }}</td>
                  <td class="text-end">{{ formatCurrency(difference.current_amount) }}</td>
                  <td class="text-end" :class="{ 'text-danger': difference.difference_amount < 0 }">
                    {{ formatSignedCurrency(difference.difference_amount) }}
                  </td>
                  <td>{{ difference.reason }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section class="mb-4">
        <div class="d-flex align-items-center gap-2 mb-2">
          <h2 class="h4 mb-0">作業化しない</h2>
          <span class="badge text-bg-secondary">{{ preview.excluded_lines.length }}件</span>
        </div>
        <div class="card">
          <div v-if="preview.excluded_lines.length" class="table-responsive">
            <table class="table table-sm mb-0">
              <tbody>
                <tr v-for="line in preview.excluded_lines" :key="line.id">
                  <td>{{ line.section_name || 'セクション外' }}</td>
                  <td>{{ line.item_name || '-' }}</td>
                  <td class="text-end">{{ formatCurrency(line.amount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="card-body text-muted">作業化しない明細はありません。</div>
        </div>
      </section>

      <div class="btn-area d-flex justify-content-end gap-2">
        <router-link
          class="btn btn-secondary"
          :to="{ name: 'EstimateDetail', params: { id: preview.estimate.id }, query: route.query }"
        >
          キャンセル
        </router-link>
        <button
          class="btn btn-success"
          type="button"
          :disabled="isSubmitting || !hasSelectedCandidates"
          @click="submitConversion"
        >
          {{ isSubmitting ? '作業を作成中...' : '作業を作成' }}
        </button>
      </div>
    </template>

    <ConfirmationModal
      ref="confirmationModal"
      :is-submitting="isSubmitting"
      @confirm="confirmConversion"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import {
  convertEstimateToWork,
  getBusinessClassifications,
  getEstimateWorkConversionPreview,
  getSelectBoxVendors
} from '@/api/axios';
import ConfirmationModal from '@/components/shared/ConfirmationModal.vue';
import { extractApiErrors, formatCurrency, formatRevisionLabel } from '@/utils/estimates';

const route = useRoute();
const router = useRouter();
const store = useStore();
const preview = ref(null);
const annualCandidates = ref([]);
const temporaryCandidates = ref([]);
const combinedAnnualCandidate = ref(null);
const combinedTemporaryCandidate = ref(null);
const creationMode = ref('individual');
const amountRoundingType = ref('floor');
const vendors = ref([]);
const businessClassifications = ref([]);
const isLoading = ref(true);
const isSubmitting = ref(false);
const confirmationModal = ref(null);
const errorSummary = ref(null);
const apiErrors = ref([]);
const validationErrors = ref([]);
const allErrors = computed(() => [...apiErrors.value, ...validationErrors.value]);
const allConversionCandidates = computed(() => [
  ...annualCandidates.value,
  ...temporaryCandidates.value
]);
const selectedAnnualCandidates = computed(() =>
  annualCandidates.value.filter(candidate => candidate.selected)
);
const selectedTemporaryCandidates = computed(() =>
  temporaryCandidates.value.filter(candidate => candidate.selected)
);
const selectedCandidateCount = computed(() =>
  selectedAnnualCandidates.value.length + selectedTemporaryCandidates.value.length
);
const hasSelectedCandidates = computed(() => selectedCandidateCount.value > 0);
const activeAnnualCandidates = computed(() => {
  if (creationMode.value === 'individual') return selectedAnnualCandidates.value;
  return combinedAnnualCandidate.value && selectedAnnualCandidates.value.length
    ? [combinedAnnualCandidate.value]
    : [];
});
const activeTemporaryCandidates = computed(() => {
  if (creationMode.value === 'individual') return selectedTemporaryCandidates.value;
  return combinedTemporaryCandidate.value && selectedTemporaryCandidates.value.length
    ? [combinedTemporaryCandidate.value]
    : [];
});
const plannedWorkCount = computed(() =>
  activeAnnualCandidates.value.length + activeTemporaryCandidates.value.length
);
const selectedConversionAmount = computed(() =>
  [...selectedAnnualCandidates.value, ...selectedTemporaryCandidates.value]
    .reduce((sum, candidate) => sum + Number(candidate.amount || 0), 0)
);
const selectedDifferenceAmount = computed(() =>
  Number(preview.value?.amounts?.revision_difference_amount || 0) - selectedConversionAmount.value
);
const hasCandidates = computed(() =>
  annualCandidates.value.length + temporaryCandidates.value.length > 0
);
const isDifferenceConversion = computed(() =>
  preview.value?.difference_context?.enabled === true
);
const needsClassificationSelection = candidate =>
  candidate.is_combined || !candidate.business_classification?.id;
const excludedDifferences = computed(() =>
  preview.value?.difference_context?.excluded_differences || []
);
const baselineEstimateLabel = computed(() => {
  const baseline = preview.value?.difference_context?.baseline_estimate;
  if (!baseline) return '';
  return `${baseline.estimate_no} ${formatRevisionLabel(baseline.revision_no)}`;
});

const scrollToErrorSummary = async () => {
  await nextTick();
  errorSummary.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  errorSummary.value?.focus({ preventScroll: true });
};

const roundedAmount = (value, roundingType) => {
  if (roundingType === 'ceil') return Math.ceil(value);
  if (roundingType === 'round') return Math.round(value);
  return Math.floor(value);
};

const distributeAmount = (amount, count, roundingType = amountRoundingType.value) => {
  if (!Number.isInteger(count) || count <= 0) return [];
  const total = Number(amount || 0);
  const baseAmount = roundedAmount(total / count, roundingType);
  const amounts = Array.from({ length: count }, () => baseAmount);
  const difference = total - baseAmount * count;
  const adjustment = difference < 0 ? -1 : 1;

  for (let offset = 0; offset < Math.abs(difference); offset += 1) {
    amounts[count - 1 - offset] += adjustment;
  }
  return amounts;
};

const normalizeAnnualCandidate = candidate => {
  const annualCount = candidate.annual_count ?? '';
  const selectAll = Number(annualCount) === 12;
  const distributed = selectAll ? distributeAmount(candidate.amount, 12) : [];
  return {
    ...candidate,
    selected: true,
    m_business_classification_id: candidate.business_classification?.id || '',
    work_frequency: candidate.work_frequency || '',
    annual_count: annualCount,
    service_count: candidate.service_count ?? '',
    execution_months_pending: false,
    is_outsourced: false,
    m_vendor_id: '',
    request_amount: '',
    months: (candidate.available_months || []).map((month, index) => ({
      ...month,
      selected: selectAll,
      bill_amount: selectAll ? distributed[index] : ''
    }))
  };
};

const normalizeTemporaryCandidate = candidate => ({
  ...candidate,
  selected: true,
  m_business_classification_id: candidate.business_classification?.id || '',
  work_dt: '',
  billing_period: '',
  is_outsourced: false,
  m_vendor_id: '',
  request_amount: ''
});

const fetchPreview = async () => {
  isLoading.value = true;
  apiErrors.value = [];
  try {
    const [previewResponse, vendorsResponse, classificationsResponse] = await Promise.all([
      getEstimateWorkConversionPreview(route.params.id),
      getSelectBoxVendors(),
      getBusinessClassifications({ deleted_status: 'active' })
    ]);
    preview.value = previewResponse.data.preview;
    annualCandidates.value = (preview.value.annual_candidates || []).map(normalizeAnnualCandidate);
    temporaryCandidates.value = (preview.value.temporary_candidates || []).map(normalizeTemporaryCandidate);
    vendors.value = vendorsResponse.data || [];
    businessClassifications.value = classificationsResponse.data || [];
    refreshCombinedCandidates({ reset: true });
  } catch (error) {
    apiErrors.value = extractApiErrors(error);
  } finally {
    isLoading.value = false;
  }
};

const selectedMonths = candidate => candidate.months.filter(month => month.selected);
const monthAmountTotal = candidate => selectedMonths(candidate)
  .reduce((sum, month) => sum + Number(month.bill_amount || 0), 0);

const rebalanceMonths = candidate => {
  const selected = selectedMonths(candidate);
  const amounts = distributeAmount(candidate.amount, selected.length);
  selected.forEach((month, index) => { month.bill_amount = amounts[index]; });
  candidate.months.filter(month => !month.selected).forEach(month => { month.bill_amount = ''; });
};

const annualCountChanged = candidate => {
  const count = Number(candidate.annual_count);
  if (count === 12) {
    candidate.months.forEach(month => { month.selected = true; });
  } else if (Number.isInteger(count) && count >= 0) {
    selectedMonths(candidate).slice(count).forEach(month => { month.selected = false; });
  }
  rebalanceMonths(candidate);
};

const monthsChanged = candidate => {
  rebalanceMonths(candidate);
};

const annualSchedulePendingChanged = candidate => {
  if (candidate.execution_months_pending) {
    candidate.months.forEach(month => {
      month.selected = false;
      month.bill_amount = '';
    });
    return;
  }
  annualCountChanged(candidate);
};

const workYearChanged = candidate => {
  const startYear = Number(candidate.work_year);
  if (!Number.isInteger(startYear)) return;
  const startMonth = candidate.months[0]?.work_month || 1;
  candidate.months.forEach((month, index) => {
    month.work_year = startYear + (startMonth + index > 12 ? 1 : 0);
  });
};

const commonCandidateValue = (candidates, getter) => {
  const values = candidates.map(getter).filter(value => value !== null && value !== undefined && value !== '');
  const uniqueValues = [...new Set(values.map(value => String(value)))];
  return uniqueValues.length === 1 ? values[0] : '';
};

const combinedBreakdown = candidates => candidates
  .map(candidate => candidate.breakdown || candidate.name)
  .filter(Boolean)
  .join('\n\n');

const refreshCombinedAnnualCandidate = ({ reset = false } = {}) => {
  const candidates = selectedAnnualCandidates.value;
  if (candidates.length === 0) {
    combinedAnnualCandidate.value = null;
    return;
  }

  const first = candidates[0];
  const previous = reset ? null : combinedAnnualCandidate.value;
  const previousMonths = previous?.months || [];
  const combined = {
    ...first,
    candidate_key: 'combined:annual',
    candidate_keys: candidates.map(candidate => candidate.candidate_key),
    is_combined: true,
    selected: true,
    name: previous?.name || preview.value?.estimate?.title || first.name,
    amount: candidates.reduce((sum, candidate) => sum + Number(candidate.amount || 0), 0),
    full_amount: candidates.reduce((sum, candidate) => sum + Number(candidate.full_amount || 0), 0),
    baseline_amount: candidates.reduce((sum, candidate) => sum + Number(candidate.baseline_amount || 0), 0),
    breakdown: combinedBreakdown(candidates),
    m_business_classification_id: previous?.m_business_classification_id || commonCandidateValue(
      candidates,
      candidate => candidate.business_classification?.id
    ),
    work_frequency: previous?.work_frequency ?? commonCandidateValue(
      candidates,
      candidate => candidate.work_frequency
    ),
    annual_count: previous?.annual_count ?? commonCandidateValue(
      candidates,
      candidate => candidate.annual_count
    ),
    service_count: previous?.service_count ?? commonCandidateValue(
      candidates,
      candidate => candidate.service_count
    ),
    work_year: previous?.work_year ?? first.work_year,
    execution_months_pending: previous?.execution_months_pending || false,
    is_outsourced: previous?.is_outsourced || false,
    m_vendor_id: previous?.m_vendor_id || '',
    request_amount: previous?.request_amount ?? '',
    months: first.months.map(month => {
      const previousMonth = previousMonths.find(item =>
        item.work_year === month.work_year && item.work_month === month.work_month
      );
      return {
        ...month,
        selected: previousMonth?.selected ?? month.selected,
        bill_amount: previousMonth?.bill_amount ?? month.bill_amount
      };
    })
  };
  combinedAnnualCandidate.value = combined;
  if (!combined.execution_months_pending) rebalanceMonths(combined);
};

const refreshCombinedTemporaryCandidate = ({ reset = false } = {}) => {
  const candidates = selectedTemporaryCandidates.value;
  if (candidates.length === 0) {
    combinedTemporaryCandidate.value = null;
    return;
  }

  const first = candidates[0];
  const previous = reset ? null : combinedTemporaryCandidate.value;
  combinedTemporaryCandidate.value = {
    ...first,
    candidate_key: 'combined:temporary',
    candidate_keys: candidates.map(candidate => candidate.candidate_key),
    is_combined: true,
    selected: true,
    name: previous?.name || preview.value?.estimate?.title || first.name,
    amount: candidates.reduce((sum, candidate) => sum + Number(candidate.amount || 0), 0),
    full_amount: candidates.reduce((sum, candidate) => sum + Number(candidate.full_amount || 0), 0),
    baseline_amount: candidates.reduce((sum, candidate) => sum + Number(candidate.baseline_amount || 0), 0),
    breakdown: combinedBreakdown(candidates),
    m_business_classification_id: previous?.m_business_classification_id || commonCandidateValue(
      candidates,
      candidate => candidate.business_classification?.id
    ),
    work_dt: previous?.work_dt || first.work_dt,
    billing_period: previous?.billing_period || first.billing_period,
    is_outsourced: previous?.is_outsourced || false,
    m_vendor_id: previous?.m_vendor_id || '',
    request_amount: previous?.request_amount ?? ''
  };
};

const refreshCombinedCandidates = options => {
  refreshCombinedAnnualCandidate(options);
  refreshCombinedTemporaryCandidate(options);
};

const selectionChanged = () => {
  validationErrors.value = [];
  refreshCombinedCandidates();
};

const creationModeChanged = () => {
  validationErrors.value = [];
  refreshCombinedCandidates();
};

const amountRoundingTypeChanged = () => {
  annualCandidates.value.forEach(rebalanceMonths);
  if (combinedAnnualCandidate.value) rebalanceMonths(combinedAnnualCandidate.value);
};

const validate = () => {
  const errors = [];
  if (!hasSelectedCandidates.value) {
    errors.push('作成する見積項目を1件以上選択してください');
  }

  activeAnnualCandidates.value.forEach(candidate => {
    const prefix = `年間予定「${candidate.name}」`;
    const count = Number(candidate.annual_count);
    const workYear = Number(candidate.work_year);
    const selected = selectedMonths(candidate);
    if (candidate.is_combined && !candidate.name?.trim()) errors.push('まとめて作成する年間予定の作業名を入力してください');
    if (!candidate.m_business_classification_id) errors.push(`${prefix}の業務区分を選択してください`);
    if (!Number.isInteger(count) || count < 1 || count > 12) {
      errors.push(`${prefix}の年間実施回数は1～12回で入力してください`);
    }
    if (!candidate.execution_months_pending && selected.length === 0) errors.push(`${prefix}の実施月を選択してください`);
    if (!Number.isInteger(workYear) || workYear < 2000 || workYear > 2100) {
      errors.push(`${prefix}の作業年度は2000～2100年で入力してください`);
    }
    if (!candidate.execution_months_pending && Number.isInteger(count) && selected.length !== count) {
      errors.push(`${prefix}の選択月数（${selected.length}）と年間実施回数（${count}）が一致していません`);
    }
    if (!candidate.execution_months_pending && selected.some(month => month.bill_amount === '' || !Number.isInteger(Number(month.bill_amount)) || Number(month.bill_amount) < 0)) {
      errors.push(`${prefix}の月別金額を0円以上の整数で入力してください`);
    } else if (!candidate.execution_months_pending && monthAmountTotal(candidate) !== Number(candidate.amount)) {
      errors.push(`${prefix}の月別金額合計を年額に一致させてください`);
    }
    if (candidate.is_outsourced && !candidate.m_vendor_id) errors.push(`${prefix}の外注業者を選択してください`);
    if (candidate.is_outsourced && (
      candidate.request_amount === '' ||
      !Number.isInteger(Number(candidate.request_amount)) ||
      Number(candidate.request_amount) < 0
    )) {
      errors.push(`${prefix}の外注金額を入力してください`);
    }
  });

  activeTemporaryCandidates.value.forEach(candidate => {
    const prefix = `臨時作業「${candidate.name}」`;
    if (candidate.is_combined && !candidate.name?.trim()) errors.push('まとめて作成する臨時作業の作業名を入力してください');
    if (!candidate.m_business_classification_id) errors.push(`${prefix}の業務区分を選択してください`);
    if (!candidate.work_dt) errors.push(`${prefix}の作業日を入力してください`);
    if (!/^\d{4}-\d{2}$/.test(candidate.billing_period || '')) errors.push(`${prefix}の請求月を入力してください`);
    if (candidate.is_outsourced && !candidate.m_vendor_id) errors.push(`${prefix}の外注業者を選択してください`);
    if (candidate.is_outsourced && (
      candidate.request_amount === '' ||
      !Number.isInteger(Number(candidate.request_amount)) ||
      Number(candidate.request_amount) < 0
    )) {
      errors.push(`${prefix}の外注金額を入力してください`);
    }
  });

  validationErrors.value = [...new Set(errors)];
  return validationErrors.value.length === 0;
};

const candidateIdentityPayload = candidate => candidate.is_combined
  ? {
      candidate_keys: candidate.candidate_keys,
      name: candidate.name,
      m_business_classification_id: candidate.m_business_classification_id
    }
  : {
      candidate_key: candidate.candidate_key,
      ...(!candidate.business_classification?.id && {
        m_business_classification_id: candidate.m_business_classification_id
      })
    };

const annualPayload = candidate => ({
  ...candidateIdentityPayload(candidate),
  work_frequency: candidate.work_frequency || null,
  annual_count: candidate.annual_count,
  service_count: candidate.service_count === '' ? null : candidate.service_count,
  work_year: candidate.work_year,
  amount_rounding_type: amountRoundingType.value,
  execution_months_pending: candidate.execution_months_pending,
  months: candidate.execution_months_pending ? [] : selectedMonths(candidate).map(month => ({
    work_year: month.work_year,
    work_month: month.work_month,
    bill_amount: Number(month.bill_amount)
  })),
  is_outsourced: candidate.is_outsourced,
  m_vendor_id: candidate.is_outsourced ? candidate.m_vendor_id : null,
  request_amount: candidate.is_outsourced ? candidate.request_amount : null
});

const temporaryPayload = candidate => {
  const [billingYear, billingMonth] = (candidate.billing_period || '-').split('-');
  return {
    ...candidateIdentityPayload(candidate),
    work_dt: candidate.work_dt,
    billing_year: billingYear,
    billing_month: billingMonth,
    is_outsourced: candidate.is_outsourced,
    m_vendor_id: candidate.is_outsourced ? candidate.m_vendor_id : null,
    request_amount: candidate.is_outsourced ? candidate.request_amount : null
  };
};

const submitConversion = async () => {
  apiErrors.value = [];
  if (isSubmitting.value) return;
  if (!validate()) {
    await scrollToErrorSummary();
    return;
  }

  confirmationModal.value?.show({
    action: 'convert',
    title: '作業作成確認',
    description: isDifferenceConversion.value
      ? `選択した${selectedCandidateCount.value}項目から${plannedWorkCount.value}件の追加作業を作成します。既存作業は変更しません。よろしいですか？`
      : `選択した${selectedCandidateCount.value}項目から${plannedWorkCount.value}件の作業を作成します。よろしいですか？`,
    confirmLabel: '作業を作成する',
    submittingLabel: '作成中...',
    confirmButtonClass: 'btn-success'
  });
};

const confirmConversion = async ({ action }) => {
  if (action !== 'convert' || isSubmitting.value) return;

  let submissionFailed = false;
  try {
    isSubmitting.value = true;
    const workConversions = [
      ...activeAnnualCandidates.value.map(annualPayload),
      ...activeTemporaryCandidates.value.map(temporaryPayload)
    ];
    await convertEstimateToWork(
      preview.value.estimate.id,
      preview.value.estimate.lock_version,
      workConversions
    );
    store.dispatch('setFlashMessage', {
      message: isDifferenceConversion.value
        ? '見積の追加・増額差分から作業を作成しました'
        : '見積から年間予定・臨時作業を作成しました',
      type: 'success'
    });
    router.push({
      name: 'EstimateDetail',
      params: { id: preview.value.estimate.id },
      query: route.query
    });
  } catch (error) {
    apiErrors.value = extractApiErrors(error);
    submissionFailed = true;
  } finally {
    isSubmitting.value = false;
    await nextTick();
    confirmationModal.value?.closeModal();
  }

  if (submissionFailed) await scrollToErrorSummary();
};

const targetLabel = target => ({ annual: '年間予定', temporary: '臨時作業', none: '作業化しない' })[target] || target;
const targetBadgeClass = target => ({
  annual: 'text-bg-primary',
  temporary: 'text-bg-warning',
  none: 'text-bg-secondary'
})[target] || 'text-bg-secondary';
const formatSignedCurrency = value => {
  const amount = Number(value || 0);
  return `${amount > 0 ? '+' : ''}${formatCurrency(amount)}`;
};
const formatDate = value => value ? String(value).slice(0, 10) : '-';

onMounted(() => {
  document.title = '作業作成プレビュー';
  fetchPreview();
});
</script>

<style scoped>
.work-conversion-preview {
  width: 95%;
  max-width: 1500px;
}

.error-summary {
  scroll-margin-top: 12px;
}

.amount-table th {
  width: 58%;
}

.amount-table td {
  text-align: right;
}

.candidate-card {
  border-color: #cfd8e3;
}

.conversion-settings-card {
  border-color: #9eb6ce;
}

.candidate-selection {
  display: flex;
  align-items: center;
  cursor: pointer;
  background: #fff;
}

.candidate-selection:has(input:checked) {
  border-color: #0d6efd !important;
  background: #f3f7ff;
}

.field-label {
  display: block;
  margin-bottom: 0.25rem;
  color: #6c757d;
  font-size: 0.82rem;
}

.required-label::after {
  content: ' *';
  color: #dc3545;
}

.pre-line {
  white-space: pre-line;
}

.month-table {
  min-width: 500px;
}

.month-table th:first-child,
.month-table td:first-child {
  width: 75px;
}

.month-table th:nth-child(2),
.month-table td:nth-child(2) {
  width: 180px;
}
</style>
